// Types pour la gestion des dépenses et comptabilité

/**
 * Catégorie de dépense
 */
export enum ExpenseCategory {
  MATERIALS = 'MATERIALS',      // Matériaux
  FUEL = 'FUEL',                // Carburant
  TRAVEL = 'TRAVEL',            // Déplacements
  MEAL = 'MEAL',                // Repas
  INSURANCE = 'INSURANCE',      // Assurances
  OTHER = 'OTHER',              // Autres
}

/**
 * Statut de dépense
 */
export enum ExpenseStatus {
  PENDING = 'PENDING',          // En attente
  VALIDATED = 'VALIDATED',      // Validée
}

/**
 * Type de période pour les rapports
 */
export enum PeriodType {
  MONTH = 'MONTH',              // Mensuel
  QUARTER = 'QUARTER',          // Trimestriel
  YEAR = 'YEAR',                // Annuel
}

/**
 * Catégorie de fichier pour les justificatifs
 */
export enum FileCategory {
  EXPENSE_RECEIPT = 'EXPENSE_RECEIPT',  // Justificatif de dépense
}

// ========================================
// DTOs pour les Dépenses
// ========================================

/**
 * DTO pour créer ou modifier une dépense
 */
export interface ExpenseRequestDTO {
  label: string;                    // Libellé (requis)
  amountHt: number;                 // Montant HT (requis)
  amountTva: number;                // Montant TVA
  amountTtc: number;                // Montant TTC (requis)
  category: ExpenseCategory;        // Catégorie (requis)
  date: string;                     // Date ISO (requis) "2026-02-19"
  status?: ExpenseStatus;           // Statut (optionnel, défaut: PENDING)
  attachmentUrl?: string;           // URL du justificatif (optionnel)
  projetId?: number;                // ID du projet (optionnel)
}

/**
 * Réponse d'une dépense
 */
export interface ExpenseResponseDTO {
  id: number;
  label: string;
  amountHt: number;
  amountTva: number;
  amountTtc: number;
  category: ExpenseCategory;
  date: string;                     // ISO format
  status: ExpenseStatus;
  attachmentUrl: string | null;
  projetId: number | null;
  projetTitre: string | null;       // Titre du projet
  userId: number;
  dateCreation: string;             // ISO datetime
  dateModification: string;         // ISO datetime
}

// ========================================
// DTOs pour la Comptabilité / Rapports
// ========================================

/**
 * Détail de TVA par taux
 */
export interface TVADetailDTO {
  tauxTVA: number;                  // Taux (ex: 20.00, 10.00, 5.50)
  baseHT: number;                   // Base HT
  montantTVA: number;               // Montant de TVA
  montantTTC: number;               // Montant TTC
}

/**
 * Rapport de TVA
 */
export interface TVAReportDTO {
  tvaCollectee: TVADetailDTO[];     // TVA collectée par taux
  totalTVACollectee: number;        // Total TVA collectée
  tvaDeductible: TVADetailDTO[];    // TVA déductible par taux
  totalTVADeductible: number;       // Total TVA déductible
  tvaAReverser: number;             // TVA à reverser (peut être négatif)
  creditDeTVA: boolean;             // true si crédit de TVA
}

/**
 * Rapport financier mensuel/trimestriel/annuel
 */
export interface MonthlyReportDTO {
  periodType: PeriodType;
  year: number;
  month?: number;                   // 1-12 (null si quarter/year)
  quarter?: number;                 // 1-4 (null si month/year)
  startDate: string;                // ISO date
  endDate: string;                  // ISO date

  // Chiffre d'affaires
  chiffreAffairesHT: number;
  chiffreAffairesTTC: number;
  nombreFacturesPayees: number;

  // Dépenses
  totalDepensesHT: number;
  totalDepensesTTC: number;
  nombreDepensesValidees: number;

  // TVA
  tvaReport: TVAReportDTO;

  // Marges
  margeBrute: number;               // CA HT - Dépenses HT
  tauxMarge: number;                // (Marge / CA HT) * 100

  // Résultat
  resultatNet: number;              // CA TTC - Dépenses TTC - TVA à reverser

  // Indicateurs
  montantMoyenFacture: number;
  montantMoyenDepense: number;
}

/**
 * Marge par projet
 */
export interface ProjectMarginDTO {
  projetId: number;
  projetTitre: string;
  adresseChantier: string;
  totalFacture: number;             // Total facturé (HT)
  totalDepenses: number;            // Total dépenses (HT)
  margeBrute: number;               // Total facturé - Total dépenses
  tauxMarge: number;                // (Marge / Total facturé) * 100
  nombreFactures: number;
  nombreDepenses: number;
}

// ========================================
// DTOs pour les Exports Comptables
// ========================================

/**
 * Rapport de dépenses par catégorie
 */
export interface ExpenseCategoryReportDTO {
  category: ExpenseCategory;
  categoryLabel: string;
  expenses: ExpenseResponseDTO[];
  count: number;
  totalHt: number;
  totalTva: number;
  totalTtc: number;
}

/**
 * Rapport mensuel complet des dépenses
 */
export interface ExpenseMonthlyReportDTO {
  year: number;
  month: number;
  monthName: string;
  startDate: string;
  endDate: string;
  entreprise: string;
  siret: string;
  categories: ExpenseCategoryReportDTO[];
  totalCount: number;
  totalHt: number;
  totalTva: number;
  totalTtc: number;
  tvaDetails: TVADetailDTO[];
  dateGeneration: string;
}

// ========================================
// DTOs pour les Fichiers (Justificatifs)
// ========================================

/**
 * Information de fichier justificatif
 */
export interface FichierResponseDTO {
  id: number;
  nom: string;                      // Nom du fichier
  category: FileCategory;
  urlS3?: string;                   // URL S3 (si stockage S3)
  cheminLocal?: string;             // Chemin local (si stockage local)
  tailleFichier: number;            // Taille en octets
  mimeType: string;                 // Type MIME
  dateUpload: string;               // ISO datetime
  expenseId?: number;               // ID de la dépense
  userId: number;
}

/**
 * URL présignée pour accès sécurisé aux fichiers
 */
export interface PresignedUrlDTO {
  url: string;                      // URL présignée pour accès
  expiresIn: number;                // Durée validité en secondes (3600)
}

// ========================================
// Labels et helpers pour l'UI
// ========================================

/**
 * Labels français pour les catégories de dépenses
 */
export const ExpenseCategoryLabels: Record<ExpenseCategory, string> = {
  [ExpenseCategory.MATERIALS]: 'Matériaux',
  [ExpenseCategory.FUEL]: 'Carburant',
  [ExpenseCategory.TRAVEL]: 'Déplacements',
  [ExpenseCategory.MEAL]: 'Repas',
  [ExpenseCategory.INSURANCE]: 'Assurances',
  [ExpenseCategory.OTHER]: 'Autres',
};

/**
 * Labels français pour les statuts de dépenses
 */
export const ExpenseStatusLabels: Record<ExpenseStatus, string> = {
  [ExpenseStatus.PENDING]: 'En attente',
  [ExpenseStatus.VALIDATED]: 'Validée',
};

/**
 * Labels français pour les types de période
 */
export const PeriodTypeLabels: Record<PeriodType, string> = {
  [PeriodType.MONTH]: 'Mensuel',
  [PeriodType.QUARTER]: 'Trimestriel',
  [PeriodType.YEAR]: 'Annuel',
};

/**
 * Couleurs pour les catégories de dépenses (pour les graphiques)
 */
export const ExpenseCategoryColors: Record<ExpenseCategory, string> = {
  [ExpenseCategory.MATERIALS]: '#3B82F6',      // Bleu
  [ExpenseCategory.FUEL]: '#EF4444',          // Rouge
  [ExpenseCategory.TRAVEL]: '#F59E0B',        // Orange
  [ExpenseCategory.MEAL]: '#10B981',          // Vert
  [ExpenseCategory.INSURANCE]: '#8B5CF6',     // Violet
  [ExpenseCategory.OTHER]: '#6B7280',         // Gris
};
